"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import * as sessionReplay from "@amplitude/session-replay-browser";
import Cookies from "js-cookie";
import { analytics } from "lib/segment";

const BLOCKED_IPS = [
  "104.166.169.70",
  "115.240.143.68",
  "115.240.143.72",
  "13.39.2.166",
  "15.181.176.225",
  "15.254.38.106",
  "16.162.197.133",
  "20.171.60.120",
  "20.171.87.53",
  "20.118.170.138",
  "20.94.198.181",
  "203.126.165.78",
  "207.7.140.232",
  "23.90.171.74",
  "34.176.22.34",
  "4.228.82.142",
  "4.228.82.245",
  "4.37.0.135",
  "4.37.0.149",
  "4.37.0.152",
  "4.37.0.153",
  "4.37.0.159",
  "4.53.29.77",
  "4.59.33.67",
  "4.59.33.78",
  "4.59.33.81",
  "41.198.131.167",
  "5.254.100.22",
  "64.29.128.192",
  "64.29.128.238",
  "64.29.129.197",
  "64.29.129.199",
  "64.29.134.134",
  "64.29.134.151",
  "64.29.129.214",
  "64.29.135.142",
  "64.29.135.223",
  "76.9.210.85",
  "76.9.210.94",
  "77.246.180.158",
  "80.239.156.96",
  "94.46.130.85",
  "190.103.176.198",
  "190.103.176.215",
  "207.7.140.187",
  "223.70.241.7",
  "20.94.199.255",
  "64.29.128.145",
  "70.224.224.40",
  "80.239.156.93",
  "176.111.108.7",
  "20.94.199.243",
  "34.176.13.109",
  "15.254.34.219",
  "15.181.176.35",
  "20.118.170.37",
  "16.162.221.132",
  "15.236.197.38",
  // Splunk synthetics test IPs starts
  "35.183.153.6",
  "3.98.180.207",
  "52.203.47.95",
  "54.218.70.232",
  "35.160.171.206",
  "44.205.74.85",
  "50.16.10.197",
  "52.41.64.36",
  "3.97.103.254",
  "184.72.20.44",
  "50.18.151.21",
  "13.57.46.124",
  "13.41.119.52",
  "13.239.150.53",
  "13.38.193.6",
  "18.134.195.219",
  "13.38.247.168",
  "35.180.95.130",
  "54.79.222.190",
  "3.11.110.54",
  "54.206.229.190",
  "18.60.197.127",
  "18.60.147.136",
  "18.60.113.150",
  "50.18.98.101"
  // Splunk synthetics test IPs ends
];

const robots = [
  "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/97.0.4692.71 Safari/537.36",
  "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/118.0.5993.70 Safari/537.36",
  "Mozilla/5.0 (compatible; TechTarget Analyzer/1.0; +http://techtarget.com/ )",
];

export default function Analytics() {
  const pathname = usePathname();
  const [isBlocked, setIsBlocked] = useState(true);

  const getOrCreateSessionId = (): number => {
    let sessionId = Cookies.get("analytics_session_id");
    if (!sessionId || sessionId === "0") {
      sessionId = Date.now().toString();
      Cookies.set("analytics_session_id", sessionId);
    }
    return Number(sessionId);
  };

  useEffect(() => {
    if (
      isBlocked ||
      (typeof window !== "undefined" &&
        window.OnetrustActiveGroups &&
        !window.OnetrustActiveGroups.includes("2"))
    )
      return;

    const initAmplitudeSessionReplay = async () => {
      // Wait for Segment to be ready
      await analytics.ready();

      const user = await analytics.user();
      const sessionId = getOrCreateSessionId();
      const deviceId = user.anonymousId()?.toString();

      // Initialize Session Replay
      await sessionReplay.init("53e832cc00dcacf4e8d96b1e656e9b04", {
        sessionId: sessionId,
        deviceId: deviceId,
        sampleRate: 1,
      }).promise;

      // Middleware: Sync session ID changes
      analytics.addSourceMiddleware(({ payload, next }) => {
        const storedSessionId = getOrCreateSessionId();
        const amplitudeIntegration =
          payload.obj.integrations?.["Actions Amplitude"] ||
          payload.obj.integrations?.Amplitude;

        const nextSessionId =
          (typeof amplitudeIntegration === "object" &&
            amplitudeIntegration?.session_id) ||
          0;

        if (nextSessionId && storedSessionId < (nextSessionId as number)) {
          Cookies.set("analytics_session_id", String(nextSessionId));
          sessionReplay.setSessionId(nextSessionId as string | number);
        }
        next(payload);
      });

      // Middleware: Add session replay properties to track calls
      analytics.addSourceMiddleware(({ payload, next }) => {
        if (payload.type() === "track") {
          const sessionReplayProperties =
            sessionReplay.getSessionReplayProperties();
          payload.obj.properties = {
            ...payload.obj.properties,
            ...sessionReplayProperties,
          };
        }
        next(payload);
      });
    };

    initAmplitudeSessionReplay();
  }, [isBlocked]);

  useEffect(() => {
    async function fetchIP() {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        const userAgent = navigator.userAgent;

        if (BLOCKED_IPS.includes(data.ip) || robots.includes(userAgent)) {
          setIsBlocked(true);
        } else {
          setIsBlocked(false);
        }
      } catch (error) {
        console.error("Failed to fetch IP address:", error);
      }
    }

    fetchIP();
  }, []);

  useEffect(() => {
    if (
      pathname !== "/panoptica" &&
      !isBlocked &&
      typeof window !== "undefined" &&
      (window.OnetrustActiveGroups == undefined ||
        window.OnetrustActiveGroups?.includes("2"))
    ) {
      analytics.page();
    }
  }, [pathname, isBlocked]);

  return null;
}
